import type { ConversationId } from "@redotech/effect-api-shared/ids/definitions";
import type { SendMessageInput } from "@redotech/effect-api-shared/models/shopper-ai/conversation/draft";
import { useRequiredContext } from "@redotech/react-util/context";
import { EffectApiClientContext } from "@redotech/redo-merchant-app-common/effect-api-client-provider";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { catchApiErrors } from "../db/error-handling";
import { draftKeys } from "./use-draft";

interface UseSendMessageOptions {
  conversationId: ConversationId;
}

/**
 * Hook to send a message with automatic cache invalidation
 */
export function useSendMessage({ conversationId }: UseSendMessageOptions) {
  const effectApi = useRequiredContext(EffectApiClientContext);
  const queryClient = useQueryClient();

  const sendMessage = useMutation({
    mutationFn: async (input: SendMessageInput) => {
      const client = effectApi.getClient();
      return await effectApi.runPromise(
        catchApiErrors(
          client.conversation.sendMessage({
            path: { id: conversationId },
            payload: input,
          }),
        ),
      );
    },
    onSuccess: () => {
      // Invalidate messages query so the new message appears
      void queryClient.invalidateQueries({
        queryKey: ["conversationMessages"],
      });
      // Invalidate drafts since the draft should be deleted by the backend
      void queryClient.invalidateQueries({ queryKey: draftKeys.all });
    },
  });

  return {
    sendMessage: sendMessage.mutateAsync,
    isSending: sendMessage.isPending,
  };
}
